import { Bell, BellOff, CheckCheck, ChevronRight, DoorOpen, Download, Home, Inbox, IndianRupee, KeyRound, LogOut, Megaphone, Settings, ShieldCheck, Smartphone, Ticket, UserRound, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { api, getSession, setSession, signOut } from '../lib/api';
import { disablePush, enablePush, getPushState, isStandalone, type PushState } from '../lib/push';
import { useMarkRead, useNotifications, useTickets, useVisit } from '../lib/queries';
import { ROLE_LABEL, useSession } from '../lib/session';
import { Badge, Button, Card, Empty, ErrorNote, Field, Input, PageHeader, Spinner, timeAgo } from '../ui';
import { useToast } from '../ui/toast';
import { TicketRow } from './complaints';
import { VisitDetailScreen } from './visits';

export function VisitRoute() {
  const { id = '' } = useParams();
  const q = useVisit(id);
  if (q.error) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  return <VisitDetailScreen id={id} visit={q.data} />;
}

/** Facility staff: their queue, most urgent first. */
export function StaffHome() {
  const { user } = useSession();
  const q = useTickets({ limit: 100, assignedToMe: true }, 30_000);
  const items = (q.data?.items ?? []).filter((t) => t.status !== 'closed' && t.status !== 'resolved');
  const order = { urgent: 0, high: 1, normal: 2, low: 3 } as const;
  items.sort((a, b) => Number(b.slaBreached) - Number(a.slaBreached) || order[a.priority] - order[b.priority]);
  return (
    <div className="space-y-4">
      <PageHeader title={`Hello${user.name ? `, ${user.name.split(' ')[0]}` : ''}`} subtitle="Your assigned tasks" />
      {q.isLoading ? <Spinner /> : q.error ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : items.length === 0 ? (
        <Empty icon={<Ticket className="h-8 w-8" />} title="No tasks assigned" hint="New complaints assigned to you appear here." action={<Link to="/complaints"><Button variant="secondary">See all complaints</Button></Link>} />
      ) : (
        <div className="space-y-2">{items.map((t) => <TicketRow key={t.id} t={t} showUnit />)}</div>
      )}
    </div>
  );
}

export function NotificationsScreen() {
  const nav = useNavigate();
  const q = useNotifications();
  const read = useMarkRead();
  const items = q.data?.items ?? [];
  const open = (n: (typeof items)[number]) => {
    if (!n.readAt) read.mutate({ ids: [n.id] });
    const d = n.data as { visitId?: string; ticketId?: string; noticeId?: string };
    nav(d.visitId ? `/visit/${d.visitId}` : d.ticketId ? `/complaints/${d.ticketId}` : d.noticeId ? '/notices' : '/');
  };
  return (
    <div className="space-y-4">
      <PageHeader title="Notifications" action={(q.data?.unreadCount ?? 0) > 0 ? <Button size="sm" variant="secondary" onClick={() => read.mutate({ all: true })}><CheckCheck className="h-4 w-4" /> Mark all read</Button> : undefined} />
      {q.isLoading ? <Spinner /> : q.error ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : items.length === 0 ? (
        <Empty icon={<Bell className="h-8 w-8" />} title="You're all caught up" />
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <button key={n.id} onClick={() => open(n)} className="block w-full text-left">
              <Card className={`!p-3 ${n.readAt ? '' : 'ring-2 ring-brand-200'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className={`truncate ${n.readAt ? 'font-medium' : 'font-bold'}`}>{n.title}</div>
                    <div className="mt-0.5 line-clamp-2 text-sm text-slate-600">{n.body}</div>
                  </div>
                  <div className="shrink-0 text-right text-xs text-slate-500">
                    {timeAgo(n.createdAt)}
                    {n.priority === 'p0' && <div className="mt-1"><Badge tone="red">Gate</Badge></div>}
                  </div>
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Mobile "More" tab: everything that doesn't fit the bottom bar. */
export function MoreScreen() {
  const { current, user } = useSession();
  const links: Array<{ to: string; label: string; icon: typeof Home; roles?: string[] }> = [
    ...(user.platformAdmin ? [{ to: '/platform', label: 'Society requests', icon: Inbox }] : []),
    { to: '/dues', label: 'Maintenance dues', icon: IndianRupee, roles: ['owner', 'tenant'] },
    { to: '/visitors', label: 'Visitors', icon: DoorOpen, roles: ['admin'] },
    { to: '/settings', label: 'Gate & society settings', icon: Settings, roles: ['admin'] },
    { to: '/passes', label: 'Guest passes', icon: KeyRound, roles: ['owner', 'tenant', 'family'] },
    { to: '/household', label: 'My household', icon: Users, roles: ['owner', 'tenant', 'family'] },
    { to: '/complaints', label: 'Complaints', icon: Ticket, roles: ['guard'] },
    { to: '/notices', label: 'Notice board', icon: Megaphone },
    { to: '/notifications', label: 'Notifications', icon: Bell },
    { to: '/me', label: 'Profile & settings', icon: UserRound },
  ];
  return (
    <div className="space-y-4">
      <PageHeader title="More" />
      <Card className="divide-y divide-slate-100 !p-0">
        {links.filter((l) => !l.roles || l.roles.includes(current.role)).map((l) => (
          <Link key={l.to} to={l.to} className="flex min-h-14 items-center gap-3 px-4 active:bg-slate-50">
            <l.icon className="h-5 w-5 text-brand-600" aria-hidden />
            <span className="flex-1 font-medium">{l.label}</span>
            <ChevronRight className="h-5 w-5 text-slate-400" aria-hidden />
          </Link>
        ))}
      </Card>
    </div>
  );
}

export function ProfileScreen() {
  const { user, current, memberships } = useSession();
  const toast = useToast();
  const [name, setName] = useState(user.name ?? '');
  const [saving, setSaving] = useState(false);
  const [push, setPush] = useState<PushState>('off');
  const [pushBusy, setPushBusy] = useState(false);
  const [installEvt, setInstallEvt] = useState<Event & { prompt?: () => Promise<void> } | null>(null);

  useEffect(() => {
    void getPushState().then(setPush);
    const h = (e: Event) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  async function saveName() {
    setSaving(true);
    try {
      await api('/me', { method: 'PATCH', body: { name: name.trim() }, noCommunity: true });
      toast.success('Saved');
      // Refresh the cached session so the new name shows everywhere.
      const s = getSession();
      if (s) setSession({ ...s, user: { ...s.user, name: name.trim() } });
    } catch (e) { toast.error(e); } finally { setSaving(false); }
  }

  async function togglePush() {
    setPushBusy(true);
    try {
      if (push === 'on') { await disablePush(); setPush('off'); }
      else setPush(await enablePush());
    } catch (e) { toast.error(e); } finally { setPushBusy(false); }
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Profile" subtitle={`${user.phone} · ${ROLE_LABEL[current.role]}`} />
      <Card className="space-y-3">
        <Field label="Your name"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} /></Field>
        <Button onClick={saveName} loading={saving} disabled={!name.trim() || name.trim() === user.name}>Save name</Button>
      </Card>

      <Card className="space-y-3">
        <div className="flex items-start gap-3">
          {push === 'on' ? <Bell className="mt-0.5 h-5 w-5 text-emerald-600" /> : <BellOff className="mt-0.5 h-5 w-5 text-slate-400" />}
          <div className="flex-1">
            <div className="font-semibold">Alerts on this device</div>
            <div className="text-sm text-slate-600">
              {push === 'on' && 'You will be alerted when a visitor is at your gate.'}
              {push === 'off' && 'Turn on to get gate requests and complaint updates even when the app is closed.'}
              {push === 'blocked' && 'Notifications are blocked. Allow them for this app in your phone or browser settings.'}
              {push === 'unavailable' && 'Push alerts are not configured on the server yet. You will still see updates inside the app.'}
              {push === 'unsupported' && 'This browser does not support background alerts. Keep the app open, or use the Android app.'}
              {push === 'needs-install' && 'On iPhone, alerts work after you add this app to your Home Screen.'}
            </div>
          </div>
        </div>
        {(push === 'on' || push === 'off') && <Button variant={push === 'on' ? 'secondary' : 'primary'} onClick={togglePush} loading={pushBusy}>{push === 'on' ? 'Turn off alerts' : 'Turn on alerts'}</Button>}
        {push === 'needs-install' && (
          <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
            <li>Open this page in <b>Safari</b></li><li>Tap the <b>Share</b> button</li><li>Choose <b>Add to Home Screen</b></li><li>Open <b>Aawas</b> from your Home Screen and come back here</li>
          </ol>
        )}
      </Card>

      {!isStandalone() && installEvt && (
        <Card className="flex items-center gap-3">
          <Smartphone className="h-6 w-6 text-brand-600" aria-hidden />
          <div className="flex-1"><div className="font-semibold">Install the app</div><div className="text-sm text-slate-600">Faster to open and works like a normal app.</div></div>
          <Button size="sm" onClick={() => void (installEvt as { prompt?: () => Promise<void> }).prompt?.()}><Download className="h-4 w-4" /> Install</Button>
        </Card>
      )}

      {memberships.length > 1 && (
        <Card>
          <div className="mb-2 font-semibold">Your roles</div>
          <ul className="space-y-1 text-sm text-slate-700">{memberships.map((m) => <li key={m.id}>{m.communityName} — {ROLE_LABEL[m.role]}{m.unitLabel ? ` · ${m.unitLabel}` : ''}</li>)}</ul>
        </Card>
      )}

      <Card className="space-y-2 text-sm text-slate-600">
        <div className="flex items-center gap-2 font-semibold text-slate-800"><ShieldCheck className="h-5 w-5 text-emerald-600" aria-hidden /> Your privacy</div>
        <p>Your number and visitor details are used only to run your society's gate and helpdesk. They are never sold or used for ads.</p>
      </Card>

      <Button variant="secondary" size="lg" className="w-full" onClick={() => void signOut()}><LogOut className="h-5 w-5" /> Sign out</Button>
    </div>
  );
}

export const NotAllowed = () => <Navigate to="/" replace />;
